import { useMemo } from 'react';
import { Users, TrendingUp, CreditCard, BarChart3, RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/data-display/StatCard';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { ROUTES } from '@/constants';
import { useNurturingFunnel } from '@/features/nurturing/hooks';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Credito', path: ROUTES.CREDIT_DASHBOARD },
  { label: 'Nurturing', path: ROUTES.NURTURING },
];

const COLORS = ['bg-blue-500', 'bg-blue-400', 'bg-cyan-500', 'bg-cyan-400', 'bg-emerald-500', 'bg-emerald-400'];

export function NurturingPage() {
  const { funnel, isLoading, isError, refetch } = useNurturingFunnel();
  const items = funnel ?? [];
  const first = items[0];
  const last = items[items.length - 1];

  const conversionFinal = useMemo(() => {
    if (!first || !last || first.count === 0) return 0;
    return ((last.count / first.count) * 100);
  }, [first, last]);

  if (isLoading) return (<div><PageHeader title="Embudo de Nurturing" subtitle="Conversion de usuarios" breadcrumbs={BREADCRUMBS} /><div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`s-${i.toString()}`} variant="rectangular" height={80} />)}</div></div>);
  if (isError) return (<div><PageHeader title="Embudo de Nurturing" subtitle="Conversion de usuarios" breadcrumbs={BREADCRUMBS} /><div className="flex flex-col items-center rounded-lg border border-border bg-card px-6 py-16"><AlertTriangle size={48} className="mb-4 text-muted-foreground" /><h3 className="text-lg font-semibold">Error al cargar el embudo</h3><Button className="mt-4" onClick={() => refetch()}><RefreshCw size={16} /> Reintentar</Button></div></div>);

  return (
    <div>
      <PageHeader title="Embudo de Nurturing" subtitle="Conversion de usuarios registrados a creditos" breadcrumbs={BREADCRUMBS} actions={<div className="flex items-center gap-2"><HelpButton pageKey="nurturing" /><Button onClick={() => refetch()}><RefreshCw size={16} /> Actualizar</Button></div>} />
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Registrados" value={first?.count ?? 0} icon={Users} />
          <StatCard title="Conversion Final" value={`${conversionFinal.toFixed(1)}%`} icon={TrendingUp} />
          <StatCard title="Creditos Originados" value={last?.count ?? 0} icon={CreditCard} />
          <StatCard title="Etapas" value={items.length} icon={BarChart3} />
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h3 className="mb-4 text-lg font-semibold text-foreground">Embudo de Conversion</h3>
          <div className="space-y-3">
            {items.map((stage, idx) => {
              const maxCount = first?.count ?? 1;
              const widthPct = (stage.count / maxCount) * 100;
              return (
                <div key={stage.stage} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-foreground">{stage.stage}</span>
                    <span className="tabular-nums text-muted-foreground">{stage.count.toLocaleString('es-MX')} ({stage.conversion_rate_pct}%)</span>
                  </div>
                  <div className="h-8 w-full rounded bg-muted">
                    <div className={`h-full rounded ${COLORS[idx % COLORS.length]} transition-all`} style={{ width: `${widthPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-6 py-4"><h3 className="text-lg font-semibold">Detalle por Etapa</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-muted-foreground"><th className="px-6 py-3 font-medium">Etapa</th><th className="px-6 py-3 text-right font-medium">Usuarios</th><th className="px-6 py-3 text-right font-medium">Conversion</th><th className="px-6 py-3 text-right font-medium">Drop-off</th></tr></thead>
              <tbody>
                {items.map((stage, idx) => {
                  const prev = idx > 0 ? items[idx - 1] : null;
                  const dropOff = prev ? prev.count - stage.count : 0;
                  return (
                    <tr key={stage.stage} className="border-b border-border/50">
                      <td className="px-6 py-3 font-medium text-foreground">{stage.stage}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{stage.count.toLocaleString('es-MX')}</td>
                      <td className="px-6 py-3 text-right tabular-nums">{stage.conversion_rate_pct}%</td>
                      <td className="px-6 py-3 text-right tabular-nums text-red-600">{dropOff > 0 ? `-${dropOff.toLocaleString('es-MX')}` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
